import React from 'react';
import {
  Button, Card, Table, TextInput, Select, Textarea, EmptyState, IconButton, Badge,
} from '@parsina/framework';
import { Pill, Trash2, Printer, ShieldAlert } from 'lucide-react';

import { CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { RX_ROUTES, rxRouteKey, originKey } from '../../constants/vocabulary.js';
import { rxLine } from '../../screens/Visit/Visit.shared.js';
import { CostChip } from '../common/ParsehCost.jsx';
import DrugPicker, { FreeTextChip } from './DrugPicker.jsx';

// نسخه — a practice prescription. It is printable and it is worthless, and both
// of those are on purpose (see PrintPrescription.jsx).
//
// The table is the kit's `Table`, not a `DataTable` — there is no DataTable at
// framework v0.12.4, and this list is small enough that column definitions plus
// a row action column is the whole requirement.

const emptyRow = {
  drugId: null,
  drugName: '',
  dose: '',
  route: 'oral',
  frequency: '',
  duration: '',
  quantity: '',
  instructions: '',
};

export default function RxPanel({
  items = [],
  readOnly = false,
  onAdd,
  onDelete,
  onCheckInteractions,
  interactionCost,
  interactionBusy = false,
  onOpenInteractions,
  hasInteractionResult = false,
  onPrint,
}) {
  const { t, formatNumber } = useTranslation();
  const [form, setForm] = React.useState(emptyRow);
  const [adding, setAdding] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const rows = [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const submit = async () => {
    if (!form.drugName.trim()) return;
    setSaving(true);
    try {
      await onAdd({
        // NULL when the student typed a name the formulary does not carry. It is
        // what makes the row invisible to the interaction check, and what the
        // «خارج از فهرست دارویی» chip is telling them.
        drugId: form.drugId || null,
        drugName: form.drugName.trim(),
        dose: form.dose || null,
        route: form.route || null,
        frequency: form.frequency || null,
        duration: form.duration || null,
        quantity: form.quantity ? Number(form.quantity) : null,
        instructions: form.instructions || null,
        origin: 'manual',
      });
      setForm(emptyRow);
      setAdding(false);
    } finally { setSaving(false); }
  };

  const columns = [
    {
      key: 'drug',
      header: t('visit.rx.drug'),
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong style={{ fontWeight: 700 }}>{row.drugName}</strong>
          {!row.drugId && <FreeTextChip />}
          {row.origin === 'ai' && <Badge variant="warning">{t(originKey(row.origin))}</Badge>}
        </span>
      ),
    },
    { key: 'detail', header: t('visit.rx.detail'), render: (row) => rxLine(row) || '—' },
    {
      key: 'route',
      header: t('visit.rx.route'),
      render: (row) => (row.route ? t(rxRouteKey(row.route)) : '—'),
    },
    {
      key: 'quantity',
      header: t('visit.rx.quantity'),
      align: 'end',
      render: (row) => (row.quantity ? formatNumber(row.quantity) : '—'),
    },
    ...(readOnly ? [] : [{
      key: 'actions',
      header: '',
      align: 'end',
      render: (row) => (
        <IconButton
          size={30}
          variant="ghost"
          icon={<Trash2 size={15} aria-hidden />}
          label={t('common.actions.delete')}
          onClick={() => onDelete(row.id)}
          data-testid={`rx-delete-${row.id}`}
        />
      ),
    }]),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="rx-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!readOnly && (
          <Button size="sm" onClick={() => setAdding((v) => !v)} data-testid="rx-add">
            {t('visit.rx.add')}
          </Button>
        )}
        {!readOnly && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={onCheckInteractions}
              disabled={interactionBusy || !rows.length}
              data-testid="rx-check-interactions"
            >
              <ShieldAlert size={14} aria-hidden style={{ marginInlineEnd: 6 }} />
              {interactionBusy ? t('visit.rx.checking') : t('visit.rx.check')}
            </Button>
            <CostChip cost={interactionCost} before />
            {hasInteractionResult && (
              <Button size="sm" variant="ghost" onClick={onOpenInteractions} data-testid="rx-interactions-open">
                {t('visit.rx.lastCheck')}
              </Button>
            )}
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onPrint}
          disabled={!rows.length}
          data-testid="rx-print"
        >
          <Printer size={14} aria-hidden style={{ marginInlineEnd: 6 }} />
          {t('visit.rx.print')}
        </Button>
      </div>

      {adding && !readOnly && (
        <Card style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="rx-form">
          <DrugPicker
            value={form.drugName}
            onPick={(drug) => setForm((f) => ({ ...f, drugId: drug.id, drugName: drug.name }))}
            onFreeText={(text) => setForm((f) => ({ ...f, drugId: null, drugName: text }))}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <TextInput
              label={t('visit.rx.dose')}
              value={form.dose}
              onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))}
              data-testid="rx-dose"
            />
            <Select
              label={t('visit.rx.route')}
              value={form.route}
              onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}
              options={RX_ROUTES.map((r) => ({ value: r, label: t(rxRouteKey(r)) }))}
              placeholder=""
              data-testid="rx-route"
            />
            <TextInput
              label={t('visit.rx.frequency')}
              value={form.frequency}
              onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
              data-testid="rx-frequency"
            />
            <TextInput
              label={t('visit.rx.duration')}
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
              data-testid="rx-duration"
            />
            <TextInput
              label={t('visit.rx.quantity')}
              value={form.quantity}
              inputMode="numeric"
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value.replace(/\D/g, '') }))}
              data-testid="rx-quantity"
            />
          </div>
          <Textarea
            label={t('visit.rx.instructions')}
            rows={2}
            value={form.instructions}
            onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
            data-testid="rx-instructions"
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={submit} disabled={saving || !form.drugName.trim()} data-testid="rx-submit">
              {t('common.actions.save')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setAdding(false); setForm(emptyRow); }}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </Card>
      )}

      {rows.length ? (
        <Table
          columns={columns}
          rows={rows}
          dense
          ariaLabel={t('visit.tabs.rx')}
          data-testid="rx-table"
        />
      ) : (
        <EmptyState
          icon={<Pill size={44} strokeWidth={1.5} aria-hidden />}
          title={t('visit.rx.empty')}
          description={t('visit.rx.emptyBody')}
          data-testid="rx-empty"
        />
      )}

      {rows.some((r) => !r.drugId) && (
        <p style={{ margin: 0, fontSize: 11.5, color: CT.mute }}>{t('visit.rx.freeTextNote')}</p>
      )}
    </div>
  );
}
